// GET /notifications · POST /notifications/read
import { z } from 'zod'
import { genderSchema, notificationKindSchema } from '../enums.js'
import {
  idSchema,
  isoDateTimeSchema,
  okResponseSchema,
  paginated,
  paginationQuerySchema,
} from './common.js'

/**
 * Полезная нагрузка уведомления. Раньше это был нетипизированный `Record<string, unknown>`,
 * и стороны разъехались молча: `ray` писал `from_name`, а фронт читал `partner_name` — все
 * лучики в инбоксе показывались без имени отправителя, и ни тесты, ни типы этого не ловили.
 * Поля общие для всех видов, необязательные там, где вид их не заполняет.
 */
export const notificationPayloadSchema = z.object({
  link_id: z.string().nullish(),
  partner_name: z.string().nullish(),
  partner_avatar_url: z.string().nullish(),
  /**
   * Пол того, О КОМ строка, а не того, кто её читает. Строка лучика в инбоксе говорит о
   * его отправителе («послал{|а} лучик»), и без этого поля она была женской для всех:
   * «Дима · ✨ послала тебе лучик». Пол у отправителя известен на сервере, но до фронта
   * не доезжал вовсе — поэтому поле, а не догадка на клиенте.
   */
  partner_gender: genderSchema.nullish(),
  /**
   * Текст лучика либо готовая реплика Луни.
   *
   * У лучика это поле **не вечное**: свободный текст стирает джоб `ray_text_expire` через
   * `RAY_TEXT_TTL_HOURS` (см. `domain/rayRetention.ts` и legal.md §5). Фраза из библиотеки
   * не стирается — её узнают по `compliment_id`.
   */
  text: z.string().nullish(),
  /**
   * Какая фраза из курируемой библиотеки была выбрана. Не переписка, а выбор из закрытого
   * списка, поэтому идентификатор остаётся навсегда — и именно он говорит уборщику «эту
   * строку не трогать».
   */
  compliment_id: z.string().nullish(),
  /**
   * Свободный текст был и его стёрли по сроку хранения.
   *
   * Признак обязателен, потому что без него стёртый лучик неотличим от лучика, отправленного
   * вовсе без слов (и то и другое — пустой `text` без `compliment_id`), а показывать их нужно
   * по-разному: «прилетел лучик» против «отправил тебе комплимент».
   */
  text_erased: z.boolean().nullish(),
  /** Веха «вы вместе N дней». */
  days: z.number().int().nullish(),
})
export type NotificationPayload = z.infer<typeof notificationPayloadSchema>

export const notificationDtoSchema = z.object({
  id: idSchema,
  kind: notificationKindSchema,
  payload: notificationPayloadSchema,
  read_at: isoDateTimeSchema.nullable(),
  created_at: isoDateTimeSchema,
})
export type NotificationDto = z.infer<typeof notificationDtoSchema>

// GET /notifications
export const listNotificationsQuerySchema = paginationQuerySchema
export type ListNotificationsQuery = z.infer<typeof listNotificationsQuerySchema>
export const listNotificationsResponseSchema = paginated(notificationDtoSchema)
export type ListNotificationsResponse = z.infer<typeof listNotificationsResponseSchema>

// POST /notifications/read — конкретные id, либо all: true для «прочитать всё».
export const readNotificationsRequestSchema = z.union([
  z.object({ ids: z.array(idSchema).min(1) }),
  z.object({ all: z.literal(true) }),
])
export type ReadNotificationsRequest = z.infer<typeof readNotificationsRequestSchema>
export const readNotificationsResponseSchema = okResponseSchema
export type ReadNotificationsResponse = z.infer<typeof readNotificationsResponseSchema>
